import { useState } from 'react'
import { getTranslation } from '../../../services/translations'
import teamSpiritStateService from '../services/teamSpiritStateService'
import {
  buildTeamSpiritTimeline,
  formatDateLabel,
  formatSpirit,
  parseNumber,
} from '../teamSpiritUtils'
import { resetEvents } from '../data/teamSpiritMatchSchedule'
import { worldCupCalendar } from '../data/worldCupCalendar'
import { buildPerCountryCalculation } from '../calculations/teamSpiritCalculations'
import {
  compactMatchLabel,
  extractMatchNumberFromCalendarLabel,
  formatTournamentStatusLabel,
  getActiveNationalTeamName,
  getCalendarCellLabel,
  getCurrentCalendarPositionForNationalTeam,
  getMatchLink,
  getRoundBucketFromCalendarPosition,
  getRoundBucketFromUtils,
  getScheduleMatchRound,
  getScheduleLabel,
  matchesCompetitionTargets,
  normalizeFixtureCountryKey,
  shouldShowCalendarReset,
  shouldShowSchedule,
  toDateKey,
} from '../teamSpiritPageUtils'
import {
  attitudeStyles,
  dayLabels,
  defaultCountryValues,
  HOME_ADVANTAGE_FACTOR,
} from '../teamSpiritConstants'
import useTeamSpiritPageState from './useTeamSpiritPageState'
import { getOpponentFromMatch } from '../data/teamSpiritGroupMapping'

const cardStyle = {
  backgroundColor: '#f7f8fa',
  color: '#1f2328',
  borderColor: '#d7dbe0',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
}

const tableHeadStyle = {
  backgroundColor: '#e9eef3',
  color: '#1f2328',
  whiteSpace: 'nowrap',
  fontSize: '11px',
  padding: '4px 6px',
  lineHeight: 1.1,
}

const compactCellStyle = { fontSize: '12px', padding: '4px 6px', lineHeight: 1.2 }
const compactControlStyle = {
  fontSize: '12px',
  minHeight: '28px',
  paddingTop: '2px',
  paddingBottom: '2px',
}
const spiritInputWidth = '68px'
const resetButtonWidth = '22px'
const combinedSpiritControlWidth = `calc(${spiritInputWidth} + ${resetButtonWidth} + 4px)`
const countryBandColors = ['#fffbe8', '#eef5ff']

export const filterVisibleMatchRows = (
  matchRows,
  nationalCompetition,
  worldCompetition,
  tournamentKind,
  currentRoundBucket
) => matchRows.filter(row => {
  const label = row.schedule?.label || ''
  const selection = label.match(/WC|NC/)
    ? worldCompetition
    : nationalCompetition
  if (!shouldShowSchedule(label, selection)) return false

  const roundNumber = Number(String(currentRoundBucket || '').replace('R', ''))
  const kind = String(tournamentKind || '').toUpperCase()
  if (kind === 'CC' || kind === 'CL') {
    if (!label.includes(kind)) return false
    if (!currentRoundBucket) return false
    const matchRound = Number(row.matchRound)
    if (!Number.isFinite(matchRound)) return false
    return String(currentRoundBucket).startsWith('Alku')
      ? matchRound <= 10
      : matchRound > 10
  }
  if (!Number.isFinite(roundNumber) || !['WC', 'NC'].includes(kind)) return true

  if (kind === 'NC') {
    return (
      label.includes('NC') &&
      getRoundBucketFromUtils(kind, row.matchRound) === currentRoundBucket
    )
  }

  const activeRoundLabel = `${kind} krs ${roundNumber}`
  return label.includes(kind) && label.includes(activeRoundLabel)
})

export const getPossessionPercentages = (leftInput, rightInput) => {
  const left = parseNumber(leftInput)
  const right = parseNumber(rightInput)
  const total = left !== null && right !== null ? left + right : null
  return {
    left: total > 0 ? (left / total) * 100 : null,
    right: total > 0 ? (right / total) * 100 : null,
  }
}

export const getRoundBoundaryResets = (resetEvents, matchRows) => {
  const slots = matchRows
    .map(row => Number(row.schedule?.slot))
    .filter(Number.isFinite)
    .sort((left, right) => left - right)
  if (slots.length === 0) return []

  const previousReset = resetEvents
    .filter(event => Number(event.slot) < slots[0])
    .sort((left, right) => Number(right.slot) - Number(left.slot))[0]
  const nextReset = resetEvents
    .filter(event => Number(event.slot) > slots[slots.length - 1])
    .sort((left, right) => Number(left.slot) - Number(right.slot))[0]

  return [previousReset, nextReset].filter(Boolean)
}

export const getNextMatchRowId = (matchRows, calendarPosition) => {
  const currentSlot =
    (Number(calendarPosition?.part) - 1) * 16 * 7 +
    (Number(calendarPosition?.week) - 1) * 7 +
    (Number(calendarPosition?.day) - 1)
  return matchRows
    .filter(row => Number(row.schedule?.slot) >= currentSlot)
    .sort((left, right) => Number(left.schedule.slot) - Number(right.schedule.slot))[0]
    ?.id ?? null
}

export const orderCountryGroupsForNationsCup = ({
  countryGroups,
  selectedGroupTeams,
  activeNationalTeamName,
  tournamentKind,
  nextMatchRound,
}) => {
  if (String(tournamentKind).toUpperCase() !== 'NC') return countryGroups

  const activeTeam = selectedGroupTeams.find(
    team => String(team?.name || '').trim() === activeNationalTeamName
  )
  const nextMatch = Array.isArray(activeTeam?.matches)
    ? activeTeam.matches.find(
        match => Number(match?.matchRound) === Number(nextMatchRound)
      )
    : null
  const nextOpponent = selectedGroupTeams.find(
    team => Number(team?.teamId) === Number(getOpponentFromMatch(nextMatch, activeTeam?.teamId).teamId)
  )
  const nextOpponentName = String(nextOpponent?.name || '').trim()

  if (!activeNationalTeamName || !nextOpponentName) return countryGroups

  return [
    activeNationalTeamName,
    nextOpponentName,
    ...countryGroups.filter(
      country =>
        country !== activeNationalTeamName && country !== nextOpponentName
    ),
  ]
}

export { getScheduleMatchRound } from '../teamSpiritPageUtils'

export const applyGroupMatchRounds = (matchRows, tournamentKind) =>
  matchRows.map(row => ({
    ...row,
    matchRound:
      getScheduleMatchRound(row.schedule?.label, tournamentKind) ||
      row.matchRound ||
      null,
  }))

export const getTimelineStartDate = (matchRows, groupMatches, tournamentKind) => {
  const firstScheduledRow = matchRows.find(row => row.schedule) || null
  const expectedRound = getScheduleMatchRound(
    firstScheduledRow?.schedule?.label,
    tournamentKind
  )
  const groupMatch = Array.isArray(groupMatches)
    ? groupMatches.find(match => Number(match?.matchRound) === expectedRound)
    : null
  return toDateKey(groupMatch?.matchDate) || '2026-04-17'
}

export const getNextRoundSummary = (tournamentKind, roundBucket, matchNumber) => {
  const kind = String(tournamentKind || '').toUpperCase()
  if (roundBucket && Number.isFinite(matchNumber)) return `${kind} ${roundBucket}, O${matchNumber}`
  return roundBucket ? `${kind} ${roundBucket}` : '—'
}

const getCountryBandStyle = (countryGroups, country, baseStyle = {}) => {
  const countryIndex = countryGroups.indexOf(country)
  return countryIndex < 0
    ? baseStyle
    : { ...baseStyle, backgroundColor: countryBandColors[countryIndex % 2] }
}

const useTeamSpiritViewModel = ({
  user,
  language,
  oauthToken,
  nationalTeamId,
  isU21 = false,
}) => {
  const pageState = useTeamSpiritPageState(oauthToken, nationalTeamId)
  const { group, fixtures, rows, ui, actions } = pageState
  const t = key => getTranslation(language, key)
  const [postMatchSync, setPostMatchSync] = useState({ loading: false, message: null })
  const [forceLatestRoundSimulation, setForceLatestRoundSimulation] = useState(false)
  const canSyncPreviousRound =
    [3023, 3064].includes(Number(nationalTeamId)) &&
    Boolean(user?.staff?.some(staff => staff?.role === 'HeadScout'))
  const formatPostMatchSyncResult = (prefix, result) => {
    if (Object.prototype.hasOwnProperty.call(result || {}, 'refreshedTeams')) {
      const failedTeams = Array.isArray(result?.failures) ? result.failures.length : 0
      const failureText = failedTeams > 0 ? `, epäonnistui: ${failedTeams}` : ''
      return `${prefix}: ${result?.refreshedTeams || 0} maajoukkueen henki haettu${failureText}.`
    }
    const details = [
      `${prefix}: ${result?.updatedGroups || 0} lohko(a)`,
      `tallennetut ottelutilat: ${result?.updatedGroupMatches || 0}`,
      `simuloitu: ${result?.simulatedMatches || 0}`,
      `valmis simulointi käytössä: ${result?.reusedSimulations || 0}`,
      `onnistuneet ottelut: ${result?.processedMatches || 0}`,
    ]
    const refreshedGroups = Number(result?.groupRefresh?.updatedGroups || 0)
    if (refreshedGroups > 0) details.unshift(`haetut lohkot: ${refreshedGroups}`)
    const failedMatches = Number(result?.skippedMatches || 0)
    if (failedMatches > 0) details.push(`epäonnistui: ${failedMatches}`)
    return `${details.join(', ')}. Lataa sivu uudelleen nähdäksesi tiedot.`
  }
  const simulateLatestRound = async () => {
    if (!oauthToken || !canSyncPreviousRound || postMatchSync.loading) return
    setPostMatchSync({ loading: true, message: null })
    try {
      const result = await teamSpiritStateService.simulateLatestRound(
        oauthToken,
        nationalTeamId,
        forceLatestRoundSimulation,
        group.selectedGroup
      )
      setPostMatchSync({
        loading: false,
        message: `Simuloitu: ${result.simulatedMatches || 0}, valmis simulointi käytössä: ${result.reusedSimulations || 0}, epäonnistui: ${result.skippedMatches || 0}.`,
      })
    } catch (error) {
      setPostMatchSync({
        loading: false,
        message: `Päivitys epäonnistui: ${error.response?.data?.error || error.message}`,
      })
    }
  }

  const simulatePreviousPhase = async () => {
    if (!oauthToken || !canSyncPreviousRound || postMatchSync.loading) return
    setPostMatchSync({ loading: true, message: null })
    try {
      const result = await teamSpiritStateService.simulatePreviousPhase(
        oauthToken,
        nationalTeamId,
        group.selectedGroup
      )
      await actions.reloadSelectedGroup()
      setPostMatchSync({
        loading: false,
        message: `Edellinen vaihe: simuloitu ${result.simulatedMatches || 0}, valmis simulointi käytössä ${result.reusedSimulations || 0}, epäonnistui ${result.skippedMatches || 0}.`,
      })
    } catch (error) {
      setPostMatchSync({
        loading: false,
        message: `Päivitys epäonnistui: ${error.response?.data?.error || error.message}`,
      })
    }
  }

  const fetchLatestRoundSpirits = async () => {
    if (!oauthToken || !canSyncPreviousRound || postMatchSync.loading) return
    setPostMatchSync({ loading: true, message: null })
    try {
      const result = await teamSpiritStateService.fetchLatestRoundSpirits(
        oauthToken,
        nationalTeamId
      )
      setPostMatchSync({
        loading: false,
        message: formatPostMatchSyncResult('Haettu viimeisimmän kierroksen henget', result),
      })
    } catch (error) {
      setPostMatchSync({
        loading: false,
        message: `Päivitys epäonnistui: ${error.response?.data?.error || error.message}`,
      })
    }
  }

  const currentCalendarPosition =
    getCurrentCalendarPositionForNationalTeam(nationalTeamId, isU21)
  const tournamentKind = String(group.selectedGroup?.tournamentKind || '').toUpperCase()
  const currentRoundBucket = getRoundBucketFromCalendarPosition(
    currentCalendarPosition,
    tournamentKind
  )
  const matchRowsWithGroupRounds = applyGroupMatchRounds(
    rows.matchRows,
    tournamentKind
  )
  const visibleMatchRows = group.selectedGroup
    ? filterVisibleMatchRows(
        matchRowsWithGroupRounds,
        ui.nationalCompetition,
        ui.worldCompetition,
        tournamentKind,
        currentRoundBucket
      )
    : []
  const nextMatchRowId = getNextMatchRowId(
    visibleMatchRows,
    currentCalendarPosition
  )
  const nextMatchRound = matchRowsWithGroupRounds.find(
    row => Number(row.id) === Number(nextMatchRowId)
  )?.matchRound
  const countryGroups = orderCountryGroupsForNationsCup({
    countryGroups: group.countryGroups,
    selectedGroupTeams: group.selectedGroupTeams,
    activeNationalTeamName: group.activeNationalTeamName,
    tournamentKind,
    nextMatchRound,
  })
  const timelineStartDate = getTimelineStartDate(
    visibleMatchRows,
    group.selectedGroup?.matches,
    tournamentKind
  )
  const boundaryResets = getRoundBoundaryResets(resetEvents, visibleMatchRows)
  const timelineWithoutPreviousReset = buildTeamSpiritTimeline({
    startDate: timelineStartDate,
    startingSpirit: '5.00',
    matchRows: visibleMatchRows,
    resetEvents: boundaryResets,
  })
  const firstVisibleMatchSlot = Math.min(
    ...visibleMatchRows.map(row => Number(row.schedule?.slot))
  )
  const previousReset = boundaryResets.find(
    reset => Number(reset.slot) < firstVisibleMatchSlot
  )
  const previousResetRow = previousReset && Number.isFinite(firstVisibleMatchSlot)
    ? (() => {
        const date = new Date(`${timelineStartDate}T12:00:00`)
        date.setDate(date.getDate() + Number(previousReset.slot) - firstVisibleMatchSlot)
        return {
          type: 'reset',
          key: previousReset.key,
          date,
          label: previousReset.label,
          resetFactor: previousReset.resetFactor,
          nationalTargets: previousReset.nationalTargets || null,
          worldTargets: previousReset.worldTargets || null,
        }
      })()
    : null
  const timeline = previousResetRow
    ? [previousResetRow, ...timelineWithoutPreviousReset]
    : timelineWithoutPreviousReset
  const perCountryByRowKey = buildPerCountryCalculation({
    timeline,
    matchRows: matchRowsWithGroupRounds,
    nationalCompetition: ui.nationalCompetition,
    worldCompetition: ui.worldCompetition,
    countryGroups,
    fixtureMatchByDateCountry: fixtures.byDateCountry,
    defaultCountryValues,
    activeNationalTeamName: group.activeNationalTeamName,
    normalizeFixtureCountryKey,
    toDateKey,
    matchesCompetitionTargets,
    homeAdvantageFactor: HOME_ADVANTAGE_FACTOR,
  })
  const currentCalendarLabel = getCalendarCellLabel(
    currentCalendarPosition,
    group.selectedGroup?.tournamentKind
  )
  const currentCalendarMatchNumber = extractMatchNumberFromCalendarLabel(
    currentCalendarLabel,
    group.selectedGroup?.tournamentKind
  )
  const nextRoundSummary = getNextRoundSummary(
    tournamentKind,
    currentRoundBucket,
    currentCalendarMatchNumber
  )
  const selectedGroupTeamName =
    group.activeNationalTeamName ||
    getActiveNationalTeamName(user, group.activeNationalTeamId) ||
    group.selectedGroupTeams.find(team => Number(team?.teamId) === Number(group.selectedGroup?.selectedTeamId))?.name ||
    group.selectedGroupTeams.find(team => Number(team?.teamId) === group.activeNationalTeamId)?.name ||
    'Maajoukkue'
  const nextMatchTimelineRow = timeline.find(
    row => row.type === 'match' && Number(row.index) === Number(nextMatchRowId)
  )
  const nextMatchFixture = fixtures.byDate[
    toDateKey(nextMatchTimelineRow?.date)
  ]
  const nextMatchSelection = nextMatchTimelineRow?.schedule?.label?.match(/CC|CL/)
    ? ui.nationalCompetition
    : ui.worldCompetition
  const nextMatchLabel = nextMatchTimelineRow?.schedule?.label
    ? compactMatchLabel(
        getScheduleLabel(nextMatchTimelineRow.schedule.label, nextMatchSelection)
      )
    : null
  const nextMatchDetails = nextMatchLabel
    ? nextMatchFixture?.opponentName
      ? `${nextMatchLabel}: ${selectedGroupTeamName} – ${nextMatchFixture.opponentName}`
      : nextMatchLabel
    : null
  const possession = getPossessionPercentages(
    ui.possessionLeftInput,
    ui.possessionRightInput
  )
  const setPossessionFromMidfield = (country, value) => {
    const parsedValue = parseNumber(value)
    if (parsedValue === null) return
    const normalized = Number(parsedValue).toFixed(2)
    if (country === group.activeNationalTeamName) actions.setPossessionLeftInput(normalized)
    else {
      actions.setPossessionRightInput(normalized)
      actions.setPossessionOpponentName(country)
    }
  }

  return {
    cardStyle,
    summary: { t, selectedGroupTeamName, formatTournamentStatusLabel, selectedGroup: group.selectedGroup, activeTournament: group.activeTournament, nextRoundSummary, nextMatchDetails },
    controls: {
      selectedGroupId: group.selectedGroupId,
      selectedTournamentId: group.selectedTournamentId,
      groups: group.tournamentGroups,
      tournaments: group.tournaments,
      language,
      showUpdateDays: ui.showUpdateDays,
      showPreviousPhase: ui.showPreviousPhase,
      setSelectedGroupId: actions.setSelectedGroupId,
      setSelectedTournamentId: actions.selectTournament,
      setShowUpdateDays: actions.setShowUpdateDays,
      setShowPreviousPhase: actions.setShowPreviousPhase,
      canSyncPreviousRound,
      simulateLatestRound,
      simulatePreviousPhase,
      forceLatestRoundSimulation,
      setForceLatestRoundSimulation,
      fetchLatestRoundSpirits,
      postMatchSync,
    },
    possession: {
      leftInput: ui.possessionLeftInput, rightInput: ui.possessionRightInput,
      opponentName: ui.possessionOpponentName,
      leftPercent: possession.left,
      rightPercent: possession.right,
      setLeftInput: actions.setPossessionLeftInput, setRightInput: actions.setPossessionRightInput,
    },
    table: {
      timeline, perCountryByRowKey, countryGroups,
      selectedGroupHasActiveNationalTeam: group.selectedGroupHasActiveNationalTeam,
      activeNationalTeamName: group.activeNationalTeamName,
      selectedGroup: group.selectedGroup,
      previousPhaseMatches: group.selectedGroup?.previousPhaseMatches || [],
      fixtures, nationalCompetition: ui.nationalCompetition, worldCompetition: ui.worldCompetition,
      showUpdateDays: ui.showUpdateDays, currentRoundBucket, nextMatchRowId,
      currentDateKey: toDateKey(new Date()),
      styles: { tableHeadStyle, compactCellStyle, compactControlStyle, spiritInputWidth, resetButtonWidth, combinedSpiritControlWidth },
      formatters: { t, formatDateLabel, formatSpirit, compactMatchLabel, getScheduleLabel, getMatchLink, toDateKey, normalizeFixtureCountryKey, getRoundBucketFromUtils, matchesCompetitionTargets },
      constants: { attitudeStyles, defaultCountryValues },
      actions: { ...actions, setPossessionFromMidfield, getCountryBandStyle: (country, style) => getCountryBandStyle(group.countryGroups, country, style) },
    },
    calendar: { worldCupCalendar, nationalCompetition: ui.nationalCompetition, worldCompetition: ui.worldCompetition, tournamentKind, currentRoundBucket, currentCalendarPosition, dayLabels, shouldShowCalendarReset },
    loadError: ui.loadError,
  }
}

export default useTeamSpiritViewModel
